import * as tslib_1 from "tslib";
import { ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CompanyApi } from '@api/companies.api';
import { SegmentApi } from '@api/segments.api';
import { MaintenanceTypeService } from '@core/components/inputs/maintenance-type/maintenance-type.service';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { DataRangeValidation } from '@validators/date-range-validation';
import { DateValidation } from '@validators/date.validation';
import { sortBy } from 'lodash';
import { merge, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CustomMessagesService } from '../../../core/errors/custom-messages.service';
import { mapLinksInput } from '../../../core/utils/map-links-input/map-links-Input';
import { IntegerValueValidation } from '../../../core/validators/integer-value.validation';
import { RecurrenceService } from '../services/recurrence.service';
import english from './../i18n/en.json';
import spanish from './../i18n/es.json';
import portuguese from './../i18n/pt.json';
import { DialogAlertComponent } from './dialog-alert/dialog-alert.component';
var DialogRecurrencesComponent = /** @class */ (function () {
    function DialogRecurrencesComponent(dialogData, matDialogRef, fuseTranslationLoaderService, fuseProgressBarService, matDialog, messageService, recurrenceService, formBuilder, companyApi, maintenanceTypeService, segmentApi, changeDetectorRef) {
        this.dialogData = dialogData;
        this.matDialogRef = matDialogRef;
        this.fuseTranslationLoaderService = fuseTranslationLoaderService;
        this.fuseProgressBarService = fuseProgressBarService;
        this.matDialog = matDialog;
        this.messageService = messageService;
        this.recurrenceService = recurrenceService;
        this.formBuilder = formBuilder;
        this.companyApi = companyApi;
        this.maintenanceTypeService = maintenanceTypeService;
        this.segmentApi = segmentApi;
        this.changeDetectorRef = changeDetectorRef;
        this.subscriptions = new Subject();
        this.isLoading = false;
        this.isUpdate = false;
        this.refreshDatatable = false;
        this.disableLinks = false;
        this.maintenanceTypes = [];
        this.fuseTranslationLoaderService.loadJsonTranslations({ english: english, portuguese: portuguese, spanish: spanish });
        this.recurrence = this.dialogData.recurrence;
        this.isViewMode = this.dialogData.isViewMode || this.recurrence.locked;
        this.isUpdate = !!this.recurrence && !!this.recurrence.id;
        this.isCopyMode = this.dialogData.isCopyMode;
        this.recurrencesReload = this.dialogData.recurrencesReload;
        var disabled = this.isUpdate;
        var repeat = this.recurrence.repeat || {};
        this.recurrenceForm = this.formBuilder.group({
            location: [{ disabled: disabled, value: this.recurrence.location }, Validators.required],
            segment: [{ value: this.recurrence.segment, disabled: this.isUpdate }, Validators.required],
            maintenanceType: [{ disabled: disabled, value: this.recurrence.maintenanceType }, Validators.required],
            message: [this.recurrence.message, [Validators.required, Validators.maxLength(1500)]],
            company: [{ disabled: true, value: this.recurrence.company }],
            from: new FormControl({ disabled: disabled, value: repeat.from || new Date() }, [Validators.required]),
            every: [{ disabled: disabled, value: repeat.every || 1 }, [Validators.required, Validators.min(1), Validators.max(360), IntegerValueValidation.validate]],
            interval: [{ disabled: disabled, value: repeat.interval || 'daily' }, Validators.required],
            weekday: [{ disabled: disabled, value: repeat.weekday || 'monday' }],
            dayOfMonth: [{ disabled: disabled, value: repeat.dayOfMonth || 1 }, [Validators.min(1), Validators.max(28), IntegerValueValidation.validate]],
            skipWeekend: [{ disabled: disabled, value: repeat.skipWeekend || false }, Validators.required],
            until: [{ disabled: disabled, value: repeat.until }, [DateValidation.validate]],
            newEquipments: new FormControl(this.recurrence.equipments),
            locationSectors: new FormControl(this.recurrence.locationSectors),
            locationEnvironments: new FormControl(this.recurrence.locationEnvironments)
        }, {
            validators: [DataRangeValidation.validate]
        });
        if (this.isViewMode) {
            this.recurrenceForm.disable();
        }
        if (this.isCopyMode) {
            this.recurrenceForm.updateValueAndValidity();
            this.recurrenceForm.markAllAsTouched();
        }
    }
    DialogRecurrencesComponent.prototype.ngOnInit = function () {
        var _this = this;
        var maintenanceTypeControl = this.recurrenceForm.get('maintenanceType');
        var segmentControl = this.recurrenceForm.get('segment');
        var locationControl = this.recurrenceForm.get('location');
        var companyControl = this.recurrenceForm.get('company');
        var locationChanges = locationControl.valueChanges;
        var segmentChanges = segmentControl.valueChanges;
        var maintenanceTypeChanges = maintenanceTypeControl.valueChanges;
        var segmentOrLocationChanges = merge(segmentChanges, locationChanges);
        var segmentOrLocationOrMaintenanceTypeChanges = merge(segmentChanges, locationChanges, maintenanceTypeChanges);
        this.recurrenceForm.valueChanges.pipe(takeUntil(this.subscriptions))
            .subscribe(function () {
            _this.disableLinks = !locationControl.value || !segmentControl.value;
        });
        locationChanges.pipe(takeUntil(this.subscriptions))
            .subscribe(function () {
            _this.changeDetectorRef.detectChanges();
        });
        segmentOrLocationChanges.pipe(takeUntil(this.subscriptions))
            .subscribe(function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            var selectedSegmentId, selectedLocationId, recurrenceSegmentId, recurrenceLocationId, isSameSegment, isSameLocation;
            return tslib_1.__generator(this, function (_a) {
                selectedSegmentId = segmentControl.value && segmentControl.value.id;
                selectedLocationId = locationControl.value && locationControl.value.id;
                recurrenceSegmentId = this.recurrence && this.recurrence.segment && this.recurrence.segment.id;
                recurrenceLocationId = this.recurrence && this.recurrence.location && this.recurrence.location.id;
                isSameSegment = selectedSegmentId === recurrenceSegmentId;
                isSameLocation = selectedLocationId === recurrenceLocationId;
                if (!isSameSegment || !isSameLocation) {
                    this.recurrenceForm.get('newEquipments').patchValue([]);
                    this.recurrenceForm.get('locationSectors').patchValue([]);
                    this.recurrenceForm.get('locationEnvironments').patchValue([]);
                }
                return [2 /*return*/];
            });
        }); });
        segmentOrLocationOrMaintenanceTypeChanges.pipe(takeUntil(this.subscriptions), map(function () {
            return {
                segmentId: segmentControl.value && segmentControl.value.id,
                locationId: locationControl.value && locationControl.value.id,
                maintenanceTypeId: maintenanceTypeControl.value && maintenanceTypeControl.value.id
            };
        }), tap(function () {
            companyControl.setValue(null);
            _this.fuseProgressBarService.show();
        }), filter(function (_a) {
            var segmentId = _a.segmentId, locationId = _a.locationId, maintenanceTypeId = _a.maintenanceTypeId;
            return segmentId && locationId && maintenanceTypeId;
        }), switchMap(function (_a) {
            var segmentId = _a.segmentId, locationId = _a.locationId, maintenanceTypeId = _a.maintenanceTypeId;
            return _this.companyApi.getCompanyByConfig({ locationId: locationId, segmentId: segmentId, maintenanceTypeId: maintenanceTypeId });
        }), tap(function () {
            _this.fuseProgressBarService.hide();
        }), filter(function (company) { return !!company; }), tap(function (company) {
            companyControl.setValue(company);
        })).subscribe();
        segmentChanges.subscribe(function (segment) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            var segmentMaintenanceTypes, selectedMaintenanceType, _a, selectedMaintenanceTypeIsOnList;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.getSelectedSegmentMaintenanceTypes(segment)];
                    case 1:
                        segmentMaintenanceTypes = _b.sent();
                        selectedMaintenanceType = this.recurrence.maintenanceType;
                        if (!segmentMaintenanceTypes.length) return [3 /*break*/, 2];
                        this.maintenanceTypes = segmentMaintenanceTypes;
                        return [3 /*break*/, 4];
                    case 2:
                        _a = this;
                        return [4 /*yield*/, this.getAllMaintenanceTypes()];
                    case 3:
                        _a.maintenanceTypes = _b.sent();
                        _b.label = 4;
                    case 4:
                        if (selectedMaintenanceType) {
                            selectedMaintenanceTypeIsOnList = this.maintenanceTypes.some(function (maintenanceType) {
                                return maintenanceType.id === selectedMaintenanceType.id;
                            });
                            if (!selectedMaintenanceTypeIsOnList) {
                                this.maintenanceTypes.push(selectedMaintenanceType);
                                this.maintenanceTypes = sortBy(this.maintenanceTypes, ['name']);
                            }
                        }
                        return [2 /*return*/];
                }
            });
        }); });
        segmentChanges.pipe(takeUntil(this.subscriptions))
            .subscribe(function (segment) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                if (segment && segment.id && !this.isUpdate) {
                    maintenanceTypeControl.enable();
                }
                else {
                    maintenanceTypeControl.disable();
                }
                return [2 /*return*/];
            });
        }); });
        if (this.isUpdate) {
            var currentMaintenanceTypeIsOnList = this.maintenanceTypes.some(function (maintenanceType) {
                return maintenanceType.id === _this.recurrence.maintenanceType.id;
            });
            if (!currentMaintenanceTypeIsOnList) {
                this.maintenanceTypes.push(this.recurrence.maintenanceType);
                this.maintenanceTypes = sortBy(this.maintenanceTypes, ['name']);
            }
        }
        if (this.recurrence && this.recurrence.id) {
            this.recurrenceForm.markAllAsTouched();
        }
        this.recurrenceForm.statusChanges.subscribe(function () {
            setTimeout(function () {
                _this.isFormValid = _this.recurrenceForm.valid;
            });
        });
    };
    DialogRecurrencesComponent.prototype.getAllMaintenanceTypes = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var allMaintenanceTypes;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.maintenanceTypeService.listMaintenanceTypes({
                            where: {
                                archived: false,
                                type_in: ['preventiveMaintenance', 'customMaintenance']
                            }
                        })];
                    case 1:
                        allMaintenanceTypes = _a.sent();
                        return [2 /*return*/, allMaintenanceTypes];
                }
            });
        });
    };
    DialogRecurrencesComponent.prototype.getSelectedSegmentMaintenanceTypes = function (segment) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var maintenanceTypes;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!segment) {
                            return [2 /*return*/, []];
                        }
                        return [4 /*yield*/, this.segmentApi.searchOne(segment.id)];
                    case 1:
                        maintenanceTypes = (_a.sent()).maintenanceTypes;
                        return [2 /*return*/, maintenanceTypes];
                }
            });
        });
    };
    DialogRecurrencesComponent.prototype.copyRecurrence = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var recurrence, dialogRecurrences, reload;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.isViewMode) {
                            return [2 /*return*/];
                        }
                        this.matDialogRef.close(false);
                        this.fuseProgressBarService.show();
                        this.isLoading = true;
                        return [4 /*yield*/, this.recurrenceService.getById(this.recurrence.id)];
                    case 1:
                        recurrence = _a.sent();
                        delete recurrence.id;
                        dialogRecurrences = this.matDialog.open(DialogRecurrencesComponent, {
                            panelClass: 'dialog-recurrences',
                            data: {
                                recurrence: recurrence,
                                isViewMode: this.isViewMode,
                                recurrencesReload: this.recurrencesReload,
                                isCopyMode: true
                            }
                        });
                        this.isLoading = false;
                        this.fuseProgressBarService.hide();
                        this.messageService.success('RECURRENCES.DIALOG.COPIED');
                        return [4 /*yield*/, dialogRecurrences.afterClosed().toPromise()];
                    case 2:
                        reload = _a.sent();
                        if (reload) {
                            this.recurrencesReload.next();
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    DialogRecurrencesComponent.prototype.ngOnDestroy = function () {
        this.subscriptions.next();
        this.subscriptions.unsubscribe();
    };
    DialogRecurrencesComponent.prototype.openArchiveOrUnarchiveDialog = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var archiveOrUnarchiveDialog, archived;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        archiveOrUnarchiveDialog = this.matDialog.open(DialogAlertComponent, {
                            panelClass: 'dialog-alert',
                            data: this.recurrence && this.recurrence.archived
                        });
                        return [4 /*yield*/, archiveOrUnarchiveDialog.afterClosed().toPromise()];
                    case 1:
                        archived = _a.sent();
                        if (typeof archived !== 'boolean') {
                            return [2 /*return*/];
                        }
                        this.recurrence.archived = archived;
                        return [4 /*yield*/, this.saveRecurrence()];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    DialogRecurrencesComponent.prototype.saveRecurrence = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var formValue, recurrence;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.fuseProgressBarService.show();
                        this.isLoading = true;
                        formValue = this.recurrenceForm.getRawValue();
                        recurrence = {
                            locationId: formValue.location.id,
                            typeId: formValue.maintenanceType.id,
                            segmentId: formValue.segment.id,
                            message: formValue.message,
                            archived: this.recurrence && this.recurrence.archived || false,
                            repeat: {
                                from: formValue.from,
                                every: formValue.every ? parseInt(formValue.every, 10) : null,
                                interval: formValue.interval,
                                weekDay: formValue.weekday,
                                dayOfMonth: parseInt(formValue.dayOfMonth, 10),
                                skipWeekend: formValue.skipWeekend,
                                until: formValue.until
                            }
                        };
                        Object.assign(recurrence, mapLinksInput({
                            locationSectors: formValue.locationSectors,
                            locationEnvironments: formValue.locationEnvironments,
                            newEquipments: formValue.newEquipments
                        }));
                        if (this.recurrence.id) {
                            delete recurrence.repeat;
                            delete recurrence.typeId;
                        }
                        return [4 /*yield*/, this.recurrenceService.save(this.recurrence.id, recurrence)
                                .then(function (result) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                                var saved;
                                return tslib_1.__generator(this, function (_a) {
                                    switch (_a.label) {
                                        case 0:
                                            this.refreshDatatable = true;
                                            this.matDialogRef.close(true);
                                            return [4 /*yield*/, this.recurrenceService.getById(result.id)];
                                        case 1:
                                            saved = _a.sent();
                                            return [2 /*return*/];
                                    }
                                });
                            }); }).finally(function () {
                                _this.fuseProgressBarService.hide();
                                _this.isLoading = false;
                            })];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    return DialogRecurrencesComponent;
}());
export { DialogRecurrencesComponent };
